import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

// ─── My Tasks / resource calendar (z8nrz7fh6h) ───────────────────────────────
// Mirrors cgt-backend src/resource-calendar. Who sees what is decided by the
// server: without resource_calendar.read_all the response holds only the
// caller's own row and work, whatever team is asked for.

export type ActivityCode = 'EQ' | 'SA' | 'UA' | 'PR' | 'IN' | 'CL' | 'RV' | 'KT' | 'QA'
export type ActivityState = 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
export type ActivitySource = 'local' | 'maintenance' | 'process_step' | 'support' | 'unavailability'
export type DayState = 'ON' | 'OFF' | 'NOT_STARTED' | 'UNAVAILABLE'

/** One card, whatever table it came from. `key` is 'ra:<id>', 'me:<id>', … */
export interface CalendarActivity {
  key: string
  type: ActivityCode
  description: string
  source: ActivitySource
  sourceId: string | null
  sourceRef: string | null
  /** Where the record is managed in the app, when it has a screen. */
  href: string | null
  ownerId: string | null
  date: string
  dueDate: string | null
  hours: number
  startTime: string | null
  endTime: string | null
  state: ActivityState
  /** Opaque lock token; a move sends back the one it read. */
  version: string
  /** False for work managed in its own screen (PR, SA, UA). */
  movable: boolean
  /** The form a maintenance task carries; completing it completes the task. */
  formName?: string | null
}

export interface DayLoad {
  date: string
  state: DayState
  shiftCode: string | null
  shiftHrs: number
  uaHrs: number
  availableHrs: number
  assignedHrs: number
  remainingHrs: number
  overloaded: boolean
}

export interface CalendarResource {
  userId: string
  name: string
  initials: string
  role: string | null
  isSupervisor: boolean
  /** False for someone disabled or no longer in the center who still owns work. */
  active: boolean
  days: DayLoad[]
}

export interface CalendarView {
  scope: 'own' | 'all'
  canManage: boolean
  days: string[]
  resources: CalendarResource[]
  activities: CalendarActivity[]
  legend: { code: ActivityCode; name: string }[]
  /** The center's today, in its timezone. */
  today: string
  teams: { id: string; name: string }[]
}

export interface CalendarQuery {
  from?: string
  to?: string
  teamId?: string
}

export interface MoveCheck {
  errors: string[]
  warnings: string[]
}

export interface MovePayload {
  date: string
  ownerId: string | null
  version: string
}

/**
 * The types "Plan a task" offers, in the order a supervisor reaches for them.
 * Each picks from work already waiting; UA (time off) is the one typed in.
 */
export const PLANNABLE_TYPES = ['EQ', 'CL', 'PR', 'RV', 'QA', 'KT', 'IN', 'UA'] as const
export type PlannableCode = (typeof PLANNABLE_TYPES)[number]

export type LinkKind = 'review' | 'capa' | 'kit' | 'pick_list' | 'process_step'

/** A task waiting to be planned (GET /resource-calendar/pending). */
export interface PendingTask {
  type: ActivityCode
  /** 'maintenance' is planned by moving the task itself; the rest are linked. */
  kind: 'maintenance' | LinkKind
  id: string
  /** Maintenance only: the calendar key and lock token to move it with. */
  key: string | null
  version: string | null
  description: string
  sourceRef: string | null
  dueDate: string | null
  hours: number
  ownerId: string | null
  ownerName: string | null
  href: string | null
  /** Maintenance only: the day it is on now. */
  plannedDate: string | null
}

/** Plan a linked pending task, or add a time-off block (UA). */
export interface CreateActivityPayload {
  type: Exclude<PlannableCode, 'EQ' | 'CL'>
  linkKind?: LinkKind
  linkId?: string
  description?: string
  sourceRef?: string
  ownerId?: string | null
  date: string
  dueDate?: string
  hours?: number
  startTime?: string
  endTime?: string
  reason: string
}

/**
 * A refused call, with the status kept: 409 means the activity changed since
 * it was read (refresh and retry), 400 a rule refused it (`errors` says which).
 */
export class CalendarApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly errors: string[],
  ) {
    super(message)
    this.name = 'CalendarApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    let errors: string[] = []
    try {
      const parsed = JSON.parse(text) as { message?: string | string[]; errors?: string[] }
      if (parsed.message) message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      if (Array.isArray(parsed.errors)) errors = parsed.errors
    } catch {
      /* leave raw text */
    }
    throw new CalendarApiError(
      message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`,
      res.status,
      errors.length > 0 ? errors : [message],
    )
  }
  return res.json() as Promise<T>
}

const post = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body) })

export function calendarQueryString(q: CalendarQuery): string {
  const params = new URLSearchParams()
  if (q.from && q.to) {
    params.set('from', q.from)
    params.set('to', q.to)
  }
  if (q.teamId) params.set('teamId', q.teamId)
  const s = params.toString()
  return s ? `?${s}` : ''
}

const keyPath = (key: string) => `/resource-calendar/activities/${encodeURIComponent(key)}`

export const resourceCalendarApi = {
  calendar: (q: CalendarQuery) => request<CalendarView>(`/resource-calendar${calendarQueryString(q)}`),
  /** One type for "Plan a task"; none for the task queue. Non-supervisors get only their own. */
  pending: (type?: ActivityCode) =>
    request<PendingTask[]>(`/resource-calendar/pending${type ? `?type=${type}` : ''}`),
  create: (body: CreateActivityPayload) =>
    post<{ activity: CalendarActivity; warnings: string[] }>('/resource-calendar/activities', body),
  preview: (key: string, body: MovePayload) => post<MoveCheck>(`${keyPath(key)}/preview`, body),
  move: (key: string, body: MovePayload & { reason?: string }) =>
    post<{ activity: CalendarActivity; warnings: string[] }>(`${keyPath(key)}/move`, body),
}
